import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Wand2 } from "lucide-react";
import { api } from "../lib/api";

interface FallbackMessageManagerProps {
  token: string;
}

interface FallbackMessage {
  id: number;
  kbId: number | null;
  kbName: string | null;
  message: string;
  isActive: boolean;
}

interface KnowledgeBase {
  id: number;
  name: string;
}

const Modal: React.FC<{
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ open, title, onClose, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <h3 className="font-semibold text-lg">{title}</h3>
          <button className="text-gray-500 hover:text-gray-700" onClick={onClose}>✕</button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
      </div>
    </div>
  );
};

const FallbackMessageManager: React.FC<FallbackMessageManagerProps> = ({ token }) => {
  const [messages, setMessages] = useState<FallbackMessage[]>([]);
  const [kbs, setKbs] = useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const [kbId, setKbId] = useState<string>(""); // "" => all KBs
  const [isActiveDraft, setIsActiveDraft] = useState<boolean>(false);

  const resetModal = () => {
    setEditId(null);
    setMessage("");
    setKbId("");
    setIsActiveDraft(false);
  };

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/fallback-message/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(data.messages ?? []);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to fetch fallback messages");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchKBs = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/kb/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKbs((data.knowledgeBases ?? []).map((kb: any) => ({ id: kb.id, name: kb.name })));
    } catch {
      // Non-blocking for UI; keep empty
    }
  }, [token]);

  useEffect(() => {
    fetchMessages();
    fetchKBs();
  }, [fetchMessages, fetchKBs]);

  const saveMessage = async () => {
    if (!message.trim()) return;
    setSaving(true);
    try {
      const payload: any = {
        message: message.trim(),
        isActive: isActiveDraft,
        kbId: kbId ? Number(kbId) : null,
      };

      if (editId === null) {
        await api.post(`/fallback-message`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await api.put(`/fallback-message/${editId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setOpen(false);
      resetModal();
      fetchMessages();
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to save fallback message");
    } finally {
      setSaving(false);
    }
  };

  const handleNew = () => {
    resetModal();
    setOpen(true);
  };

  const handleEdit = (m: FallbackMessage) => {
    setEditId(m.id);
    setMessage(m.message);
    setKbId(m.kbId ? String(m.kbId) : "");
    setIsActiveDraft(!!m.isActive);
    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this fallback message?")) return;
    setSaving(true);
    try {
      await api.delete(`/fallback-message/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchMessages();
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to delete fallback message");
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (id: number) => {
    setSaving(true);
    try {
      await api.put(`/fallback-message/${id}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchMessages();
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to activate fallback message");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-8 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold">Fallback Messages</h2>
        <button
          onClick={handleNew}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" /> New Message
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded">
          {error}
        </div>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : (
        <div className="border rounded divide-y">
          {messages.map((m) => (
            <div key={m.id} className="p-4 flex items-start justify-between hover:bg-gray-50">
              <div className="space-y-1 w-3/5">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-500 font-mono">ID: {m.id}</span>
                  <span className="text-xs text-gray-500">
                    Scope: {m.kbId ? (m.kbName ?? `KB ${m.kbId}`) : "All KBs"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {m.isActive && (
                    <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700">Active</span>
                  )}
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{m.message}</p>
              </div>

              <div className="flex gap-2 shrink-0">
                <button onClick={() => handleEdit(m)} className="p-2 rounded hover:bg-gray-100" title="Edit">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => handleDelete(m.id)} className="p-2 rounded hover:bg-gray-100" title="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
                {!m.isActive && (
                  <button onClick={() => handleActivate(m.id)} className="p-2 rounded hover:bg-gray-100" title="Activate">
                    <Wand2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {messages.length === 0 && (
            <div className="p-6 text-gray-600">No fallback messages yet.</div>
          )}
        </div>
      )}

      <Modal
        title={editId ? "Edit Fallback Message" : "New Fallback Message"}
        open={open}
        onClose={() => {
          if (!saving) {
            setOpen(false);
            resetModal();
          }
        }}
      >
        <div className="space-y-1">
          <label className="text-sm font-medium">Scope</label>
          <select
            className="border rounded px-3 py-2 w-full"
            value={kbId}
            onChange={(e) => setKbId(e.target.value)}
            disabled={saving}
          >
            <option value="">All KBs (user-level)</option>
            {kbs.map((kb) => (
              <option key={kb.id} value={kb.id}>
                {kb.name} (KB {kb.id})
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Message</label>
          <textarea
            className="border rounded px-3 py-2 w-full h-24"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={saving}
            placeholder="Fallback message shown when the model lacks context."
          />
        </div>

        <div className="flex items-center gap-2 pt-2">
          <input
            id="isActive"
            type="checkbox"
            className="h-4 w-4"
            checked={isActiveDraft}
            onChange={(e) => setIsActiveDraft(e.target.checked)}
            disabled={saving}
          />
          <label htmlFor="isActive" className="text-sm">
            Set active for this scope
          </label>
        </div>

        <div className="pt-4 flex justify-end gap-2">
          <button
            className="px-3 py-1 rounded border"
            onClick={() => {
              if (!saving) {
                setOpen(false);
                resetModal();
              }
            }}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="px-4 py-1 rounded text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
            onClick={saveMessage}
            disabled={saving || !message.trim()}
          >
            {editId ? "Save" : "Create"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default FallbackMessageManager;
